import React, { useEffect, useMemo, useRef, useState } from 'react';
import WithNavigation from '../../layouts/WithNavigation';
import Heading from '../../components/Heading';
import Row from '../../components/Row';
import Col from '../../components/Col';
import StatusOverviewCard from './components/StatusOverviewCard';
import DaysSinceSubmissionCard from './components/DaysSinceSubmissionCard';
import Loader from '../../components/Loader';
import { ServiceRequestDashboardTable } from './components/ServiceRequestDashboardTable';
import { ServiceRequestDashboardActions } from './components/ServiceRequestDashboardActions';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useBranding } from 'src/branding/BrandingContextProvider';
import {
  deleteServiceRequestFromGroup,
  deleteServiceRequestGroup,
  updateBulkStatus,
} from 'src/services/client';
import { toast } from 'react-toastify';
import { FilterOverlay } from './components/Overlays/FilterOverlay';
import { GroupingOverlay } from './components/Overlays/GroupingOverlay';
import { DeclinedStatusOverlay } from './components/Overlays/BulkStatus/DeclinedStatusOverlay';
import { AssignedStatusOverlay } from './components/Overlays/BulkStatus/AssignedStatusOverlay';
import { FilterBar } from './components/FilterBar';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { useServiceRequestDashboardApi } from 'src/services/api/ServiceRequestDashboard/serviceRequestDashboard';
import {
  filterServiceRequests,
  searchServiceRequests,
} from './helpers/filterServiceRequests';
import { PAGINATION_LIMIT } from 'src/common/constants';
import {
  selectFilters,
  selectFormattedFiltersForApi,
} from 'src/store/selectors/serviceRequestDashboard/filters';
import { applyFilter } from 'src/store/actions/serviceRequestDashboard/filters';
import Overlay from '../../components/Overlay';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { DownloadCSVAndPDFParserOverlay } from 'src/utils/DownloadCSVParser/DownloadCSVParser';
import FontAwesomeIcon from 'src/components/Icon';

export default () => {
  const { branding } = useBranding();
  const user = useSelector(selectAuthenticatedUser);
  const [searchString, setSearchString] = useState<string>('');
  const [pagination, setPagination] = useState<number>(1);
  const [selectedServiceRequests, setSelectedServiceRequests] = useState<
    string[]
  >([]);
  const [allSelected, setAllSelected] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const {
    fetchDashboard,
    fetchAllOverviews,
    serviceRequests,
    statusOverview,
    ageOverview,
    loading,
  } = useServiceRequestDashboardApi();

  const filteredServiceRequests = useMemo(() => {
    return filterServiceRequests(serviceRequests, {
      searchString,
      pagination: Number(PAGINATION_LIMIT) * pagination,
    });
  }, [serviceRequests, searchString, pagination]);

  const [showGroupingOverlay, setShowGroupingOverlay] = useState(false);
  const [groupingPropertyId, setGroupingPropertyId] = useState(null);
  const [showDeclinedStatusOverlay, setShowDeclinedStatusOverlay] =
    useState(false);
  const [showAssignedStatusOverlay, setShowAssignedStatusOverlay] =
    useState(false);
  const [showDownloadOverlay, setShowDownloadOverlay] = useState(false);
  const [showFilterOverlay, setShowFilterOverlay] = useState(false);

  const formattedFilters = useSelector(selectFormattedFiltersForApi);

  const fetchData = () => {
    setSelectedServiceRequests([]);
    fetchAllOverviews(formattedFilters);
    fetchDashboard(formattedFilters);
  };

  const handleSelectServiceRequests = (ids: any) => {
    if (allSelected) {
      setAllSelected(false);
      setSelectedServiceRequests(
        serviceRequests.reduce((acc: any[], curr: IServiceRequest) => {
          if (!ids.includes(curr.id)) {
            acc.push(curr.id);
          }

          return acc;
        }, [])
      );
    } else {
      if (selectedServiceRequests.some((r: string) => ids.includes(r))) {
        setSelectedServiceRequests(
          selectedServiceRequests.filter((r: string) => !ids.includes(r))
        );
      } else {
        setSelectedServiceRequests(selectedServiceRequests.concat(ids));
      }
    }
  };

  const { Statuses } = useSelector(selectFilters);
  const dispatch = useDispatch();

  const handleToggleShowResolved = () => {
    const resolved: boolean = !showResolved;
    setShowResolved(resolved);

    if (resolved) {
      dispatch(applyFilter('Statuses', ['Resolved', 'Closed', 'Declined']));
    } else {
      dispatch(
        applyFilter(
          'Statuses',
          Statuses.filter(
            (s: any) => s !== 'Resolved' && s !== 'Closed' && s !== 'Declined'
          )
        )
      );
    }
  };

  // Grouping.
  const handleDeleteGroup = (groupId: any) => {
    if (window.confirm('Are you sure you want to delete this group?')) {
      deleteServiceRequestGroup(groupId, user.id).then(() =>
        fetchDashboard(formattedFilters)
      );
    }
  };

  const handleDeleteItemFromGroup = (itemId: any) => {
    if (
      window.confirm(
        `Are you sure you want to remove this ${branding?.text?.serviceRequest?.toLowerCase()} from the group?`
      )
    ) {
      deleteServiceRequestFromGroup(itemId, user.id).then(() =>
        fetchDashboard(formattedFilters)
      );
    }
  };

  const handleBulkStatusUpdate = (status: any) => {
    if (selectedServiceRequests.length === 0 && !allSelected) {
      return;
    }

    const sr: string[] = allSelected
      ? searchServiceRequests(serviceRequests, searchString).map((s) => s.id)
      : selectedServiceRequests;

    const params = {
      serviceRequest: sr.map((s) => ({ id: s })),
      name: status,
      userId: user.id,
    };

    switch (status) {
      case 'Declined':
        setShowDeclinedStatusOverlay(true);
        // Show declined popup?
        break;
      case 'Assigned':
        setShowAssignedStatusOverlay(true);
        // Show assigned popup?
        break;
      default:
        updateBulkStatus(params).then((res: any) => {
          if (res.status === 200) {
            toast.success('Service requests updated successfully', {
              autoClose: 5000,
              position: 'top-center',
            });
            fetchDashboard(formattedFilters);
            fetchAllOverviews(formattedFilters);
          } else {
            toast.error(
              '🚫 Failed to update service requests, please try again later.',
              { autoClose: 5000, position: 'top-center' }
            );
          }
        });
        break;
    }
  };

  const selectedServiceRequestObjects = () =>
    serviceRequests?.filter((r: IServiceRequest): boolean => {
      return selectedServiceRequests.includes(r.id);
    }) ?? [];

  const handleOpenGroupingOverlay = () => {
    if (selectedServiceRequests.length === 0) {
      toast.error('Please select at least one service request to group.', {
        autoClose: 6000,
        position: 'top-center',
      });
      return;
    }

    if (allSelected) {
      toast.error('Please select service requests individually to group.', {
        autoClose: 6000,
        position: 'top-center',
      });
      return;
    }

    if (selectedServiceRequests.length === 1) {
      setShowGroupingOverlay(true);
      return;
    }

    const propertyId: string =
      selectedServiceRequestObjects()[0].propertyId ?? '';
    setGroupingPropertyId(propertyId);

    if (
      selectedServiceRequestObjects().find(
        (r: IServiceRequest): boolean => r.propertyId !== propertyId
      )
    ) {
      toast.error(
        'Please select service requests from the same property to group.',
        { autoClose: 6000, position: 'top-center' }
      );
      return;
    }

    setShowGroupingOverlay(true);
  };

  useEffect(() => {
    fetchData();
  }, [JSON.stringify(formattedFilters)]);

  return (
    <WithNavigation
      id="service-request-dashboard"
      title="Service Requests"
      testId="service-request-dashboard"
      top={
        <Heading headline="Service Requests">
          <Row chonky>
            <Col xs={12} md={6} chonky>
              <StatusOverviewCard
                serviceRequestOverview={statusOverview}
                onSelectStatus={() => {
                  setShowResolved(false);
                }}
              />
            </Col>
            <Col xs={12} md={6} chonky>
              <DaysSinceSubmissionCard serviceRequestOverview={ageOverview} />
            </Col>
          </Row>
        </Heading>
      }
    >
      <Overlay showOverlay={loading} transparent={true} centre>
        <Loader style={{ margin: 0 }} />
      </Overlay>
      <div className="tabs">
        <FilterOverlay
          showFilterOverlay={showFilterOverlay}
          setShowFilterOverlay={setShowFilterOverlay}
        />
        <GroupingOverlay
          showGroupOverlay={showGroupingOverlay}
          setShowGroupOverlay={setShowGroupingOverlay}
          selectedServiceRequestIds={selectedServiceRequests}
          groupingPropertyId={groupingPropertyId}
          fetchDashboard={() => fetchData()}
          existingGroups={[]}
        />
        <DeclinedStatusOverlay
          showDeclinedStatusOverlay={showDeclinedStatusOverlay}
          setShowDeclinedStatusOverlay={setShowDeclinedStatusOverlay}
          serviceRequestIds={allSelected ? serviceRequests.map((r: IServiceRequest) => r.id) : selectedServiceRequests}
          fetchDashboard={fetchData}
        />
        <AssignedStatusOverlay
          showAssignedStatusOverlay={showAssignedStatusOverlay}
          setShowAssignedStatusOverlay={setShowAssignedStatusOverlay}
          serviceRequests={allSelected ? serviceRequests : selectedServiceRequestObjects()}
          fetchDashboard={fetchData}
        />
        <DownloadCSVAndPDFParserOverlay
          showDownloadOverlay={showDownloadOverlay}
          setShowDownloadOverlay={setShowDownloadOverlay}
          serviceRequestIds={allSelected ? serviceRequests.map((r: IServiceRequest) => r.id) : selectedServiceRequests}
        />
        <ServiceRequestDashboardActions
          onChangeSearchString={(searchString: string) => setSearchString(searchString)}
          filterCount={0}
          showResolved={showResolved}
          onShowResolved={() => handleToggleShowResolved()}
          onShowFilterOverlay={() => setShowFilterOverlay(true)}
          searchString={searchString}
          onBulkStatusUpdate={(status: string) => handleBulkStatusUpdate(status)}
          onGroup={() => handleOpenGroupingOverlay()}
          disableGroup={!allSelected && selectedServiceRequests.length === 0}
          onDownload={() => setShowDownloadOverlay(true)}
        />
        <FilterBar />
          <InfiniteScroll
            dataLength={filteredServiceRequests.length}
            next={() => setPagination(pagination + 1)}
            hasMore={true}
            loader={null}
            scrollableTarget="service-request-dashboard"
          >
            <ServiceRequestDashboardTable
              serviceRequests={filteredServiceRequests}
              selectedServiceRequests={selectedServiceRequests}
              onSelectServiceRequest={(id: any) => handleSelectServiceRequests([id])}
              onSelectGroup={(ids: any) => handleSelectServiceRequests(ids)}
              onDeleteGroup={(id: any) => handleDeleteGroup(id)}
              onRemoveFromGroup={(itemId: any) => handleDeleteItemFromGroup(itemId)}
              onSelectAll={() => {
                setAllSelected(!allSelected);
                if (allSelected) {
                  setSelectedServiceRequests([]);
                }
              }}
              allSelected={allSelected}
            />
          </InfiniteScroll>
        </div>
    </WithNavigation>
  );
};
